/**
 * Al Shola School – API Service
 * ==============================
 * Central API layer. Backend runs on port 9001.
 */

const BASE_URL = process.env.VUE_APP_API_BASE || 'https://0x01source.com:9002/api';

async function request(endpoint, options = {}) {
  const url = `${BASE_URL}${endpoint}`;
  const config = {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  };
  if (config.body instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  const res = await fetch(url, config);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API ${res.status}: ${text}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

function get(endpoint) { return request(endpoint); }

function post(endpoint, data) {
  const isForm = data instanceof FormData;
  return request(endpoint, { method: 'POST', body: isForm ? data : JSON.stringify(data) });
}

function put(endpoint, data) {
  const isForm = data instanceof FormData;
  return request(endpoint, { method: 'PUT', body: isForm ? data : JSON.stringify(data) });
}

function patch(endpoint, data) {
  const isForm = data instanceof FormData;
  return request(endpoint, { method: 'PATCH', body: isForm ? data : JSON.stringify(data) });
}

function del(endpoint) { return request(endpoint, { method: 'DELETE' }); }

/** Helper: extract results from DRF paginated or raw array responses */
function extractList(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}

const api = {
  extractList,

  /* ── Site Settings ── */
  getSettings: () => get('/settings/'),

  /* ── Navigation Menu ── */
  getMenu: () => get('/menu/'),
  getMenuCategories: () => get('/menu-categories/'),

  /* ── Home Page (aggregated) ── */
  getHomePage: () => get('/home/'),

  /* ── Home individual endpoints ── */
  getHeroSliders: () => get('/hero-sliders/'),
  getPrincipal: () => get('/principal/'),
  getPrograms: () => get('/programs/'),
  getFAQs: () => get('/faqs/'),
  getPartners: () => get('/partners/'),

  /* ── CMS Pages ── */
  getPages: () => get('/pages/'),
  getPageBySlug: (slug) => get(`/pages/${slug}/`),
  getPageSections: (slug) => get(`/pages/${slug}/sections/`),

  /* ── Gallery ── */
  getAlbums: () => get('/gallery/'),
  getAlbum: (id) => get(`/gallery/${id}/`),
  getAlbumPhotos: (id) => get(`/gallery/${id}/photos/`),

  /* ── Testimonials ── */
  getTestimonials: (featured = false) => get(`/testimonials/${featured ? '?featured=1' : ''}`),
  getTestimonial: (id) => get(`/testimonials/${id}/`),

  /* ── Contact ── */
  getContactPage: () => get('/contact/'),
  getContactPeople: () => get('/contact-people/'),
  submitContactForm: (data) => post('/contact-submissions/', data),

  /* ── Policies ── */
  getPolicies: () => get('/policies/'),
  getPolicy: (id) => get(`/policies/${id}/`),
  getPolicyPages: (id) => get(`/policies/${id}/pages/`),

  /* ── Admission ── */
  getAdmissionPage: () => get('/admission/'),
  getAdmissionSections: () => get('/admission-sections/'),
  getGradeEquivalencies: (level = '') => get(`/grade-equivalencies/${level ? '?level=' + level : ''}`),

  /* ── Staff ── */
  getStaff: (department = '') => get(`/staff/${department ? '?department=' + department : ''}`),

  /* ── News ── */
  getNews: (category = '') => get(`/news/${category ? '?category=' + category : ''}`),
  getNewsDetail: (slug) => get(`/news/${slug}/`),

  post, put, patch, del,
};

export default api;
